/**
 * The per-finish price adder table (UID-346, ADR-0117 D8; shared for UID-503).
 *
 * HAND-AUTHORED, NOT GENERATED. This file is a deliberate sibling of the
 * ERP-derived `manufacturing-catalog.ts` rather than part of it: the catalog is
 * regenerated wholesale from the ERP export by
 * `scripts/derive-process-material-config.mjs`, and these numbers are not in that
 * export — folding them in would mean either a hand-edit that the next
 * regeneration silently discards, or teaching the derivation script to invent
 * prices. They live apart and are edited by hand, on purpose.
 *
 * AUTHORED DEMO-GRADE DATA, and deliberately honest about it. ADR-0117 D8 fixed
 * what may and may not be taken from the design prototype's `_cfgUnit`
 * (`design_handoff_misumi_unified_platform/Fictiv Platform.dc.html:8260-8266`):
 *
 *   TAKEN — the SHAPE (a material-category base rate + a per-finish adder) and
 *   the prototype's own $4-$37 adder band.
 *
 *   NOT TAKEN — the `_hash()` JITTER. The prototype computed the adder as
 *   `(hash(finish.id) % 34) + 4`. D8 forbids porting it: "a pseudo-random price
 *   is worse than an honest flat one because it looks computed". So each of the
 *   catalog's 30 finishes carries an EXPLICITLY AUTHORED adder, ordered by
 *   real-world cost plausibility.
 *
 * All values are WHOLE DOLLARS as integers, on purpose: base + adder is then
 * exact integer addition with no float anywhere on the pricing path.
 *
 * WHY THIS IS IN `packages/shared` (UID-503). The Part Configurator's finish
 * TILES display the adder ("+$15/ea") next to each choice, and the estimate
 * CHARGES it (`MockConfigurationPricingPort#finishAdder`). Those two numbers must
 * be the same number — a tile advertising a delta the estimate does not charge is
 * a price the buyer was shown and then not given — so the table and its lookup
 * have exactly one owner, here, and both apps read it. The material-category
 * tiers stay in `apps/api` (`acl/mock/fixtures/configuration-rate-card.ts`)
 * because nothing in the UI displays them.
 *
 * REPLACED, NOT EXTENDED, when real pricing arrives. SVC§2 #2 puts pricing in its
 * own service; this table is the demo stand-in behind `ConfigurationPricingPort`,
 * which is the one swap point.
 */

/**
 * Per-finish adders for all 30 finishes the catalog offers, in the prototype's
 * $4-$37 band. EXPLICITLY AUTHORED (D8), ordered by real-world cost: light
 * mechanical operations at the bottom, multi-stage plating / hard-coat anodize
 * and heat treatment at the top.
 *
 * `finish: null` — the "as machined / standard, no secondary operation" choice —
 * adds NOTHING, matching the prototype's `f.id === '__std' → 0`. It is not a row
 * here because it is not a finish. See {@link finishPriceAdderFor}, which is the
 * lookup every caller should use rather than indexing this table directly.
 *
 * A finish absent from this table falls to {@link DEFAULT_FINISH_ADDER}. That is
 * reachable only if the ERP export grows a finish before this table is updated,
 * so it is a soft-landing, not a design.
 */
export const FINISH_ADDERS: Readonly<Record<string, number>> = {
  // Light mechanical / cosmetic.
  Sanding: 4,
  Blasting: 5,
  'Media Blasting': 5,
  'Vibratory Tumble': 5,
  'Laser Engraving': 6,
  'Black Oxide': 6,
  Priming: 7,
  Polishing: 8,
  'Hand Polishing': 10,
  'Vibratory Polishing': 9,
  'Vapor Smoothing': 9,
  'Vapor Polishing': 11,
  'Pad Printing': 8,
  // Conversion coatings / passivation.
  Passivation: 8,
  'Chem Film (AlodineTM)': 10,
  Dyeing: 9,
  // Paint / powder / e-coat.
  Painting: 13,
  'Clear Coat': 11,
  'Powder Coating': 14,
  'E-Coating (Electro Coating)': 17,
  // Anodizing.
  'Anodize per Mil-PRF-8625 Type II': 15,
  'Anodize per Mil-PRF-8625 Type III': 21,
  'Anodize per Mil-PRF-8625 Type III w/ PTFE': 27,
  // Plating.
  'Zinc Plating': 16,
  'Nickel Plating': 22,
  'ENP (Electroless Nickel Plating)': 29,
  Electropolishing: 24,
  // Heat treatment — the most expensive band.
  Tempering: 26,
  Hardening: 31,
  'Case Hardening': 37,
}

/** Adder for a finish the table does not name. See {@link FINISH_ADDERS}. */
export const DEFAULT_FINISH_ADDER = 12
